import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, ChevronDown, ChevronRight, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

const STATUS_STYLE = {
  scheduled: "bg-sky-400/15 text-sky-300",
  sent: "bg-green-500/15 text-green-400",
  failed: "bg-red-500/15 text-red-400",
  cancelled: "bg-white/10 text-white/40",
};

/** Lists scheduled messages (pending + recent history) so staff can see what's queued up
 * and cancel anything still pending before it goes out. */
export default function ScheduledMessages({ token, refreshKey }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = () => axios.get(`${API}/contact/scheduled`, H).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (refreshKey) load(); /* eslint-disable-next-line */ }, [refreshKey]);

  const cancel = async (sm) => {
    if (!window.confirm(`Cancel the scheduled message to ${sm.name || sm.email || sm.phone}?`)) return;
    try {
      await axios.delete(`${API}/contact/scheduled/${sm.id}`, H);
      toast.success("Scheduled message cancelled.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel."); }
  };

  const pending = items.filter((i) => i.status === "scheduled");
  const visible = showHistory ? items : pending;
  if (items.length === 0) return null;

  return (
    <div data-testid="scheduled-messages-panel" className="bg-[#0C0C0C] border border-white/10 rounded-lg">
      <button data-testid="scheduled-messages-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 text-left">
        {open ? <ChevronDown size={14} className="text-[#F2B705]" /> : <ChevronRight size={14} className="text-white/40" />}
        <Clock size={14} className="text-[#F2B705]" />
        <span className="text-sm font-semibold">Scheduled Messages</span>
        {pending.length > 0 && <span className="px-2 py-0.5 rounded-full bg-sky-400/15 text-sky-300 text-[10px] font-bold">{pending.length} pending</span>}
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-2">
          <label className="flex items-center gap-2 text-xs text-white/45 cursor-pointer">
            <input type="checkbox" data-testid="scheduled-show-history" checked={showHistory} onChange={(e) => setShowHistory(e.target.checked)} className="accent-[#F2B705]" />
            Show sent / failed / cancelled history too
          </label>
          {visible.length === 0 && <p className="text-white/35 text-sm">Nothing scheduled right now.</p>}
          {visible.map((sm) => (
            <div key={sm.id} data-testid={`scheduled-row-${sm.id}`} className="flex flex-wrap items-center gap-2 bg-black/30 border border-white/10 rounded-md px-3 py-2">
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_STYLE[sm.status] || "bg-white/10 text-white/50"}`}>{sm.status}</span>
              <span className="text-sm font-semibold">{sm.name || sm.email || sm.phone}</span>
              <span className="text-white/40 text-xs">{sm.method}</span>
              <span className="text-white/40 text-xs">{fmt(sm.send_at)}</span>
              <span className="text-white/60 text-xs truncate max-w-[220px]">"{sm.message}"</span>
              {sm.error && <span className="text-red-400 text-xs">— {sm.error}</span>}
              {sm.status === "scheduled" && (
                <button data-testid={`scheduled-cancel-${sm.id}`} onClick={() => cancel(sm)}
                  className="ml-auto flex items-center gap-1 text-[11px] text-white/50 hover:text-red-400">
                  <X size={11} /> Cancel
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
